<template>
  <v-card flat color="transparent" class="pa-4">
    <v-row align="center" class="mb-3">
      <v-col cols="12" md="8">
        <h2 class="font-weight-bold">Órdenes de producción</h2>
      </v-col>
      <v-col cols="12" md="4" class="text-md-right">
        <v-btn color="deep-purple" dark :to="{ name: 'empresa.produccion.orden.registrar' }">
          <v-icon left>mdi-plus</v-icon> Nueva orden
        </v-btn>
      </v-col>
    </v-row>

    <!-- Stats -->
    <v-row class="mb-4" dense>
      <v-col cols="6" sm="3">
        <v-card outlined class="pa-3 text-center">
          <div class="caption grey--text mb-1">Pendientes</div>
          <div class="text-h5 font-weight-bold" :class="stats.pendientes > 0 ? 'orange--text text--darken-1' : ''">
            {{ stats.pendientes }}
          </div>
          <div class="caption grey--text">borrador / en proceso</div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card outlined class="pa-3 text-center">
          <div class="caption grey--text mb-1">Confirmadas hoy</div>
          <div class="text-h5 font-weight-bold">{{ stats.hoy.count }}</div>
          <div class="caption green--text">{{ money(stats.hoy.costo) }}</div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card outlined class="pa-3 text-center">
          <div class="caption grey--text mb-1">Esta semana</div>
          <div class="text-h5 font-weight-bold">{{ stats.semana.count }}</div>
          <div class="caption green--text">{{ money(stats.semana.costo) }}</div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="3">
        <v-card outlined class="pa-3 text-center">
          <div class="caption grey--text mb-1">Placas esta semana</div>
          <div class="text-h5 font-weight-bold">{{ stats.semana.placas }}</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filtros por estado -->
    <div class="mb-4 d-flex flex-wrap" style="gap: 8px">
      <v-chip
        v-for="f in filtros" :key="f.value"
        :color="filtroActivo === f.value ? 'deep-purple' : ''"
        :dark="filtroActivo === f.value"
        :outlined="filtroActivo !== f.value"
        small
        @click="filtroActivo = f.value"
      >
        {{ f.label }}
        <span v-if="f.count" class="ml-1">({{ f.count }})</span>
      </v-chip>
    </div>

    <div v-if="loading" class="text-center py-10">
      <v-progress-circular indeterminate color="deep-purple" />
    </div>

    <div v-else-if="ordenesFiltradas.length === 0" class="text-center grey--text py-8">
      No hay órdenes para mostrar.
    </div>

    <template v-else>
      <div v-for="grupo in ordenesAgrupadas" :key="grupo.fecha">
        <div class="overline grey--text mt-3 mb-1 d-flex align-center" style="gap: 8px">
          {{ grupo.label }}
          <v-chip x-small outlined>
            {{ grupo.items.length }} {{ grupo.items.length === 1 ? 'orden' : 'órdenes' }}
          </v-chip>
          <span v-if="grupo.costoTotal > 0" class="caption green--text font-weight-medium text-none">
            {{ money(grupo.costoTotal) }}
          </span>
        </div>

        <v-card outlined class="mb-1">
          <v-simple-table dense>
            <tbody>
              <tr
                v-for="item in grupo.items" :key="item.id"
                class="row-pointer"
                @click="abrir(item)"
              >
                <td style="width:190px">
                  <span class="caption font-weight-medium">{{ item.numero_display }}</span>
                </td>
                <td class="caption">{{ item.receta_nombre }}</td>
                <td class="caption grey--text" style="width:160px">{{ item.maquina_nombre || '—' }}</td>
                <td class="text-right caption" style="width:70px">
                  {{ placas(item) }} <span class="grey--text">pl</span>
                </td>
                <td class="text-right caption font-weight-medium" style="width:130px">
                  {{ item.costo_insumos_total != null ? money(totalCosto(item)) : '—' }}
                </td>
                <td style="width:120px">
                  <v-chip x-small :color="estadoColor(item.estado)" dark>{{ estadoLabel(item.estado) }}</v-chip>
                </td>
                <td class="text-right caption grey--text" style="width:110px">
                  {{ horaOrFecha(item.fecha_alta) }}
                </td>
                <td style="width:44px" class="text-right pr-2" @click.stop>
                  <v-btn
                    v-if="item.estado === 'borrador' || item.estado === 'en_proceso'"
                    icon x-small
                    :to="{ name: 'empresa.produccion.cola' }"
                    title="Ir a la cola"
                  >
                    <v-icon x-small>mdi-play-circle-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>
      </div>
    </template>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'OrdenProduccionHome',
  data() {
    return {
      items: [],
      loading: false,
      filtroActivo: 'todos',
    };
  },
  computed: {
    hoyStr() {
      return new Date().toISOString().substring(0, 10);
    },
    semanaInicio() {
      const d = new Date();
      d.setDate(d.getDate() - 6);
      return d.toISOString().substring(0, 10);
    },
    filtros() {
      const count = (estado) => this.items.filter(i => i.estado === estado).length;
      return [
        { value: 'todos', label: 'Todos', count: null },
        { value: 'borrador', label: 'Pendiente', count: count('borrador') },
        { value: 'en_proceso', label: 'En proceso', count: count('en_proceso') },
        { value: 'confirmada', label: 'Confirmadas', count: count('confirmada') },
        { value: 'cancelada', label: 'Canceladas', count: count('cancelada') },
      ];
    },
    ordenesFiltradas() {
      if (this.filtroActivo === 'todos') return this.items;
      return this.items.filter(i => i.estado === this.filtroActivo);
    },
    ordenesAgrupadas() {
      const grupos = {};
      for (const item of this.ordenesFiltradas) {
        const fecha = (item.fecha_alta || '').substring(0, 10);
        if (!grupos[fecha]) grupos[fecha] = [];
        grupos[fecha].push(item);
      }
      return Object.keys(grupos)
        .sort((a, b) => b.localeCompare(a))
        .map(fecha => ({
          fecha,
          label: this.labelFecha(fecha),
          items: grupos[fecha],
          costoTotal: grupos[fecha].reduce((sum, i) => sum + (i.costo_insumos_total != null ? this.totalCosto(i) : 0), 0),
        }));
    },
    stats() {
      const confirmadas = this.items.filter(i => i.estado === 'confirmada');
      const hoy = confirmadas.filter(i => (i.fecha_confirmacion || i.fecha_alta || '').substring(0, 10) === this.hoyStr);
      const semana = confirmadas.filter(i => (i.fecha_confirmacion || i.fecha_alta || '').substring(0, 10) >= this.semanaInicio);
      return {
        pendientes: this.items.filter(i => i.estado === 'borrador' || i.estado === 'en_proceso').length,
        hoy: {
          count: hoy.length,
          costo: hoy.reduce((s, i) => s + this.totalCosto(i), 0),
        },
        semana: {
          count: semana.length,
          costo: semana.reduce((s, i) => s + this.totalCosto(i), 0),
          placas: semana.reduce((s, i) => s + this.placas(i), 0),
        },
      };
    },
  },
  methods: {
    ...mapActions('ordenProduccion', ['fetch_ordenes']),

    estadoColor(e) {
      return { borrador: 'grey', en_proceso: 'amber darken-2', confirmada: 'green', cancelada: 'red darken-1' }[e] || 'grey';
    },
    estadoLabel(e) {
      return { borrador: 'Pendiente', en_proceso: 'En proceso', confirmada: 'Confirmada', cancelada: 'Cancelada' }[e] || e;
    },
    totalCosto(item) {
      return (parseFloat(item.costo_insumos_total) || 0) + (parseFloat(item.costo_maquina_total) || 0);
    },
    placas(item) {
      return Math.round(parseFloat(item.cantidad_lotes) || 0);
    },
    money(v) {
      if (!v) return '$0';
      return '$' + Number(v).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    labelFecha(fechaStr) {
      if (!fechaStr) return '—';
      const hoy = this.hoyStr;
      const ayer = new Date();
      ayer.setDate(ayer.getDate() - 1);
      const ayerStr = ayer.toISOString().substring(0, 10);
      if (fechaStr === hoy) return 'Hoy';
      if (fechaStr === ayerStr) return 'Ayer';
      const d = new Date(fechaStr + 'T00:00:00');
      return d.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' });
    },
    horaOrFecha(fechaStr) {
      if (!fechaStr) return '—';
      const dia = fechaStr.substring(0, 10);
      const hora = fechaStr.substring(11, 16);
      return dia === this.hoyStr ? hora : fechaStr.substring(5, 16).replace('T', ' ');
    },

    async fetchItems() {
      this.loading = true;
      try {
        const res = await this.fetch_ordenes();
        this.items = res.data;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    abrir(item) {
      this.$router.push({ name: 'empresa.produccion.orden.consultar', params: { id: item.id } });
    },
  },
  created() {
    this.fetchItems();
  },
};
</script>

<style scoped>
.row-pointer >>> tbody tr:hover {
  cursor: pointer;
  background: rgba(128,128,128,0.06);
}
</style>
